import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";

import type {
  CreateMapNoteInput,
  MapNote,
  UpdateMapNoteInput,
} from "@west-marches/shared";
import {
  createCampaignMapNote,
  deleteCampaignMapNote,
  listCampaignMapNotes,
  updateCampaignMapNote,
} from "@/lib/mapNoteClient";
import { queryKeys } from "@/lib/queryKeys";
import { getBroadcastMapNoteChange } from "@/state/realtimeBroadcast";

export function useMapNoteListQuery(
  campaignId: string | null,
  enabled = true,
  options: { asPlayer?: boolean } = {},
) {
  const asPlayer = Boolean(options.asPlayer);
  return useQuery({
    queryKey: queryKeys.mapNotes.list(campaignId ?? "", asPlayer),
    queryFn: () => listCampaignMapNotes(campaignId!, { asPlayer }),
    enabled: enabled && Boolean(campaignId),
  });
}

export function useCreateMapNoteMutation(campaignId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateMapNoteInput) => createCampaignMapNote(campaignId!, input),
    onSuccess(createdMapNote) {
      queryClient.setQueryData<MapNote[]>(
        queryKeys.mapNotes.list(campaignId!),
        (current) => [...(current ?? []), createdMapNote],
      );
      getBroadcastMapNoteChange()(createdMapNote.id, "created");
    },
    onError(error, input) {
      Sentry.captureException(error, {
        tags: { area: "map-note", op: "create" },
        extra: {
          campaignId,
          q: input.q,
          r: input.r,
          color: input.color,
        },
      });
    },
  });
}

export function useUpdateMapNoteMutation(campaignId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ mapNoteId, input }: { mapNoteId: string; input: UpdateMapNoteInput }) =>
      updateCampaignMapNote(campaignId!, mapNoteId, input),
    onSuccess(updatedMapNote) {
      queryClient.setQueryData<MapNote[]>(
        queryKeys.mapNotes.list(campaignId!),
        (current) =>
          (current ?? []).map((m) => (m.id === updatedMapNote.id ? updatedMapNote : m)),
      );
      getBroadcastMapNoteChange()(updatedMapNote.id, "updated");
    },
    onError(error, variables) {
      Sentry.captureException(error, {
        tags: { area: "map-note", op: "update" },
        extra: { campaignId, mapNoteId: variables.mapNoteId, input: variables.input },
      });
    },
  });
}

export function useDeleteMapNoteMutation(campaignId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (mapNoteId: string) => deleteCampaignMapNote(campaignId!, mapNoteId),
    onSuccess(_, deletedMapNoteId) {
      queryClient.setQueryData<MapNote[]>(
        queryKeys.mapNotes.list(campaignId!),
        (current) => (current ?? []).filter((m) => m.id !== deletedMapNoteId),
      );
      getBroadcastMapNoteChange()(deletedMapNoteId, "deleted");
    },
    onError(error, mapNoteId) {
      Sentry.captureException(error, {
        tags: { area: "map-note", op: "delete" },
        extra: { campaignId, mapNoteId },
      });
    },
  });
}
